export type PriceRuleDateKey = 'start_date' | 'end_date'

type DateRangeEntry = Partial<Record<PriceRuleDateKey, string | null>>

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value)

const hasOwnKey = (obj: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key)

export const formatPriceRuleDateValue = (date?: string | null): string => {
  if (!date) return ''
  if (typeof date !== 'string') return ''
  return date.slice(0, 10)
}

const asDateRangeEntry = (value: unknown): DateRangeEntry | null => {
  if (!isPlainObject(value)) return null
  return value as DateRangeEntry
}

const readDateDataMap = (rule: any): Record<string, unknown> | null => {
  if (isPlainObject(rule?.date_data)) return rule.date_data
  const value = rule?.value
  if (!isPlainObject(value)) return null
  if (hasOwnKey(value, 'date_data') && isPlainObject(value.date_data)) {
    return value.date_data as Record<string, unknown>
  }
  return null
}

const readValueBucket = (rule: any, fieldKey: string): unknown => {
  const value = rule?.value
  if (!isPlainObject(value) || !hasOwnKey(value, fieldKey)) return null
  return value[fieldKey]
}

const collectCandidateKeys = (rule: any, field: any): string[] => {
  const raw = [
    field?.api_id,
    field?.api_code,
    field?.api_name,
    rule?.destination_platform_field?.api_id,
    rule?.destination_platform_field?.api_code,
    rule?.destination_platform_field?.api_name,
    rule?.api_id,
    rule?.api_code,
    rule?.api_name,
  ].filter((value) => value != null && value !== '')

  const keys: string[] = []
  const seen = new Set<string>()
  for (const value of raw) {
    const textValue = String(value)
    for (const key of [textValue, textValue.toLowerCase()]) {
      if (!seen.has(key)) {
        seen.add(key)
        keys.push(key)
      }
    }
  }
  return keys
}

const resolveDateRangeFromMap = (
  dateData: Record<string, unknown>,
  candidateKeys: string[]
): DateRangeEntry | null => {
  for (const dateKey of candidateKeys) {
    if (!hasOwnKey(dateData, dateKey)) continue
    const entry = asDateRangeEntry(dateData[dateKey])
    if (entry) return entry
  }
  return null
}

const readDateFromEntry = (entry: DateRangeEntry | null, key: PriceRuleDateKey): string => {
  if (!entry) return ''
  const raw = entry[key]
  return raw != null && raw !== '' ? formatPriceRuleDateValue(raw) : ''
}

/** Resolve start/end date for a saved price rule (modal edit hydrate). */
export const getRuleDateValue = (rule: any, field: any, key: PriceRuleDateKey): string => {
  const direct = rule?.[key]
  if (direct != null && direct !== '') {
    return formatPriceRuleDateValue(direct)
  }

  const candidateKeys = collectCandidateKeys(rule, field)

  const dateData = readDateDataMap(rule)
  if (dateData) {
    const fromMap = resolveDateRangeFromMap(dateData, candidateKeys)
    const fromMapDate = readDateFromEntry(fromMap, key)
    if (fromMapDate) return fromMapDate
  }

  for (const fieldKey of candidateKeys) {
    const bucket = readValueBucket(rule, fieldKey)
    if (bucket == null) continue

    const bucketEntry = asDateRangeEntry(bucket)
    const bucketDate = readDateFromEntry(bucketEntry, key)
    if (bucketDate) return bucketDate

    if (isPlainObject(bucket) && hasOwnKey(bucket, 'date_data') && isPlainObject(bucket.date_data)) {
      const nested = resolveDateRangeFromMap(bucket.date_data as Record<string, unknown>, candidateKeys)
      const nestedDate = readDateFromEntry(nested, key)
      if (nestedDate) return nestedDate
    }
  }

  return ''
}
